/**
 * @file Recalls provider display and selection helpers.
 */

import { appData as globalAppData } from "../../app/state/state.js";
import { createUserOptions } from "../../shared/ui/controls/controlOptions.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    getUserFullDisplayName,
} from "../../shared/utils/userDisplay.js";

const LEGACY_PROVIDER_VALUE_PREFIX = "legacy:";

function cleanLegacyProviderLabel(value = "") {
    return String(value || "")
        .replace(/^Legacy:\s*/i, "")
        .replace(/\s+\(legacy\)$/i, "")
        .trim();
}

function getLegacyProviderValue(legacyKey = "") {
    return legacyKey ? `${LEGACY_PROVIDER_VALUE_PREFIX}${legacyKey}` : "";
}

function isLegacyProviderValue(value = "") {
    return String(value).startsWith(LEGACY_PROVIDER_VALUE_PREFIX);
}

function getLegacyProviderKeyFromValue(value = "") {
    return isLegacyProviderValue(value) ?
        String(value).slice(LEGACY_PROVIDER_VALUE_PREFIX.length) :
        "";
}

function getLegacyProviderConfig(config = {}, legacyKey = "") {
    return (config?.recalls?.legacyProviders || [])
        .find((provider) => provider?.key === legacyKey) || null;
}

function getProviderUsers(options = {}) {
    return Array.isArray(options.users) ? options.users : (globalAppData.users || []);
}

function resolveAppProviderLabel(provider = {}, options = {}) {
    const userId = typeof provider === "string" ?
        provider :
        provider?.userId || provider?.id || "";
    const storedLabel = typeof provider === "object" ? provider?.label : "";
    const matchedUser = getProviderUsers(options)
        .find((user) => user?.id === userId);

    if (matchedUser) {
        return getUserFullDisplayName(matchedUser);
    }

    if (storedLabel && storedLabel !== userId) {
        return storedLabel;
    }

    return "";
}

/**
 * Formats a stored Recalls provider reference for display.
 * @param {object|string} provider Stored provider reference.
 * @param {object} [options={}] Display options.
 * @param {Array<object>} [options.users] User records to resolve app providers.
 * @returns {string} Display label.
 */
export function getRecallProviderDisplayLabel(provider = {}, options = {}) {
    if (typeof provider === "string" || provider?.type !== "legacy") {
        return resolveAppProviderLabel(provider, options);
    }

    const label = provider?.label || provider?.legacyKey || "";
    if (provider?.type !== "legacy") {
        return label;
    }
    const cleanLabel = cleanLegacyProviderLabel(label) ||
        cleanLegacyProviderLabel(provider.legacyKey);
    return cleanLabel ? `${cleanLabel} (legacy)` : "";
}

/**
 * Formats multiple stored Recalls provider references for display.
 * @param {Array<object>} providers Stored provider references.
 * @param {object} [options={}] Display options.
 * @param {Array<object>} [options.users] User records to resolve app providers.
 * @returns {string} Joined display text.
 */
export function getRecallProviderDisplay(providers = [], options = {}) {
    return (Array.isArray(providers) ? providers : [])
        .map((provider) => getRecallProviderDisplayLabel(provider, options))
        .filter(Boolean)
        .join(", ");
}

/**
 * Converts a stored provider reference into a select value.
 * @param {object|string} provider Stored provider reference.
 * @returns {string} Select value.
 */
export function getRecallProviderControlValue(provider) {
    if (typeof provider === "string") {return provider;}
    if (provider?.type === "legacy") {
        return getLegacyProviderValue(provider.legacyKey);
    }
    return provider?.userId || provider?.id || "";
}

/**
 * Converts stored provider references into select values.
 * @param {Array<object|string>} providers Stored provider references.
 * @returns {Array<string>} Select values.
 */
export function getRecallProviderControlValues(providers = []) {
    return (Array.isArray(providers) ? providers : [])
        .map((provider) => getRecallProviderControlValue(provider))
        .filter(Boolean);
}

/**
 * Parses a provider mirror input value.
 * @param {string} value JSON value from the hidden mirror input.
 * @returns {Array<string>} Select values.
 */
export function parseRecallProviderMirrorValue(value) {
    try {
        const parsed = JSON.parse(value || "[]");
        return (Array.isArray(parsed) ? parsed : [])
            .filter(Boolean)
            .map((entry) => String(entry));
    } catch {
        return [];
    }
}

/**
 * Normalizes Recalls provider select values.
 * @param {*} value Raw select value.
 * @returns {Array<string>} Provider select values.
 */
export function normalizeRecallProviderControlValue(value) {
    return Array.isArray(value) ?
        value.filter(Boolean).map((entry) => String(entry)) :
        [];
}

/**
 * Converts a select value into a backend provider reference.
 * @param {string} value Select value.
 * @param {object} config Runtime app config.
 * @returns {object|null} Provider payload entry.
 */
export function getRecallProviderPayloadFromValue(value, config = {}) {
    const normalized = String(value || "");
    if (!normalized) {return null;}

    if (isLegacyProviderValue(normalized)) {
        const legacyKey = getLegacyProviderKeyFromValue(normalized);
        const configured = getLegacyProviderConfig(config, legacyKey);
        const label = cleanLegacyProviderLabel(configured?.label) || legacyKey;
        if (!legacyKey || !label) {return null;}
        return {
            type: "legacy",
            userId: null,
            legacyKey,
            label,
        };
    }

    return {
        type: "app",
        userId: normalized,
    };
}

/**
 * Converts select values into backend provider references.
 * @param {Array<string>} values Select values.
 * @param {object} config Runtime app config.
 * @returns {Array<object>} Provider payload.
 */
export function getRecallProviderPayload(values = [], config = {}) {
    return normalizeRecallProviderControlValue(values)
        .map((value) => getRecallProviderPayloadFromValue(value, config))
        .filter(Boolean);
}

/**
 * Returns the current user as a default Recalls app provider when eligible.
 * @param {object} appData Runtime app data.
 * @param {object} state Runtime auth state.
 * @returns {Array<object>} Default provider payload.
 */
export function getCurrentUserRecallProviderDefault(appData = {}, state = {}) {
    const currentUserId = state.currentUser?.uid ||
        state.currentUserProfile?.id ||
        state.currentUserProfile?.uid ||
        "";
    if (!currentUserId) {return [];}

    const currentUser = state.currentUserProfile ||
        (appData.users || []).find((user) => user?.id === currentUserId) ||
        {};
    if (
        currentUser.isAppProvider !== true ||
        currentUser.isActive === false
    ) {
        return [];
    }

    return [{
        type: "app",
        userId: currentUserId,
    }];
}

function getSelectedLegacyProviderOptions(
    selectedProviders = [],
    config = {},
    selectedValues = [],
) {
    const selected = Array.isArray(selectedProviders) ? selectedProviders : [];
    const options = selected
        .filter((provider) => provider?.type === "legacy" && provider.legacyKey)
        .map((provider) => {
            const configured = getLegacyProviderConfig(config, provider.legacyKey);
            const label = cleanLegacyProviderLabel(
                configured?.label || provider.label || provider.legacyKey,
            );
            return {
                value: getLegacyProviderValue(provider.legacyKey),
                label,
                selectedOnly: true,
                unavailableLabel: "legacy",
                searchText: `${label} legacy ${provider.legacyKey}`.toLowerCase(),
            };
        });
    const existingValues = new Set(options.map((option) => option.value));
    selectedValues
        .filter((value) => isLegacyProviderValue(value) && !existingValues.has(value))
        .forEach((value) => {
            const legacyKey = getLegacyProviderKeyFromValue(value);
            const configured = getLegacyProviderConfig(config, legacyKey);
            const label = cleanLegacyProviderLabel(configured?.label || legacyKey);
            options.push({
                value,
                label,
                selectedOnly: true,
                unavailableLabel: "legacy",
                searchText: `${label} legacy ${legacyKey}`.toLowerCase(),
            });
        });
    return options;
}

/**
 * Creates the Recalls provider select, including selected-only legacy values.
 * @param {object} config Select configuration.
 * @param {object} config.appData Runtime app data.
 * @param {Array<object>} [config.selectedProviders=[]] Stored providers.
 * @returns {object} Select lifecycle handle.
 */
export function createRecallProviderSelectPreset(config) {
    const selectedValues = normalizeRecallProviderControlValue(config.value);
    const userOptions = createUserOptions(config.appData?.users || [], {
        selectedValues,
        providersOnly: true,
        includeInactive: config.includeInactive,
        labelResolver: config.labelResolver,
    });
    const legacyOptions = getSelectedLegacyProviderOptions(
        config.selectedProviders,
        config.appData?.config || {},
        selectedValues,
    );

    return createSelectControl({
        placeholder: "+ Add providers",
        ...config,
        name: config.name || "provider",
        multiple: config.multiple ?? true,
        options: [
            ...userOptions,
            ...legacyOptions,
        ],
    });
}
